<script lang="ts" setup>
import { Brand, getBrand } from '@/brand/brand'
import { lightSet } from '@/service/driver/light_set'
import { getDataBeforeUnderscore } from '@/tool/utility'
import { computed, onMounted, Ref, ref, watchEffect } from 'vue'

const brandPath: Ref<string> = ref('')
const inputSpeed = ref(0)
const inputBrightness = ref(0)

const speedBackgroundSize = computed(() => {
  const index = lightSet.getSpeed().indexOf(lightSet.lightData.LightMode.Speed)
  const speedPercent = (index / (lightSet.getSpeed().length - 1)) * 100
  return `${speedPercent}%, 100%`
})

const lightBackgroundSize = computed(() => {
  const index = lightSet.getBrightness().indexOf(lightSet.lightData.LightMode.Light)
  const lightPercent = (index / (lightSet.getBrightness().length - 1)) * 100
  return `${lightPercent}%, 100%`
})

const getName = (name: string) => getDataBeforeUnderscore(name)

/* 方向图标映射 */
const directionIcons: Record<string, string> = {
  up: 'M12 20V4 M5 11l7-7 7 7',
  down: 'M12 4v16 M5 13l7 7 7-7',
  left: 'M20 12H4 M11 5l-7 7 7 7',
  right: 'M4 12h16 M13 5l7 7-7 7',
  clockwise: 'M21 2v6h-6 M21 13a9 9 0 1 1-3-7.7L21 8',
  anticlockwise: 'M3 2v6h6 M3 13a9 9 0 1 0 3-7.7L3 8',
  counterclockwise: 'M3 2v6h6 M3 13a9 9 0 1 0 3-7.7L3 8',
  inner: 'M12 3v5 M9 5l3 3 3-3 M12 21v-5 M9 19l3-3 3 3 M3 12h5 M5 9l3 3-3 3 M21 12h-5 M19 9l-3 3 3 3',
  outer: 'M12 9V2 M9 5l3-3 3 3 M12 15v7 M9 19l3 3 3-3 M9 12H2 M5 9l-3 3 3 3 M15 12h7 M19 9l3 3-3 3',
}

const getDirectionPath = (name: string) => {
  const key = getName(name).toLowerCase()
  return directionIcons[key] || ''
}

const handleBrightnessChange = () => {
  // lightSet.lightData.LightMode.Light = inputBrightness.value
  lightSet.lightData = {
    ...lightSet.lightData,
    LightMode: { ...lightSet.lightData.LightMode, Light: inputBrightness.value },
  }
}

const handleSpeedChange = () => {
  // lightSet.lightData.LightMode.Speed = inputSpeed.value
  lightSet.lightData = {
    ...lightSet.lightData,
    LightMode: { ...lightSet.lightData.LightMode, Speed: inputSpeed.value },
  }
}

watchEffect(() => {
  const initialSpeed = lightSet.lightData && lightSet.lightData.LightMode.Speed
  if (initialSpeed !== undefined) {
    inputSpeed.value = initialSpeed
  }
})

watchEffect(() => {
  const initialBrightness = lightSet.lightData && lightSet.lightData.LightMode.Light
  if (initialBrightness !== undefined) {
    inputBrightness.value = initialBrightness
  }
})

onMounted(() => {
  getBrand().then((value: Brand) => {
    if (value === Brand.carotmas) {
      brandPath.value = 'carotmas'
    } else {
      brandPath.value = 'img'
    }
  })
})

const effectAttach = computed(() => lightSet.getEffectAttach())
</script>
<template>
  <div class="light-config-container" v-if="effectAttach">
    <div v-if="effectAttach.isSpeed" class="config-container">
      <div class="title">{{ $t('light_speed') }}</div>
      <div class="config">
        <div class="label_box">
          <span>{{ $t('light_speed_slow') }}</span>
          <span>{{ $t('light_speed_fast') }}</span>
        </div>
        <div class="slider_wrapper">
          <input
            type="range"
            v-if="lightSet.getMinSpeed() > lightSet.getMaxSpeed()"
            :min="lightSet.getMaxSpeed()"
            :max="lightSet.getMinSpeed()"
            step="1"
            class="slider rtl input_range"
            v-model.number="inputSpeed"
            :style="{ backgroundSize: speedBackgroundSize }"
            @change="handleSpeedChange"
          />

          <input type="range" v-else :min="lightSet.getMinSpeed()" :max="lightSet.getMaxSpeed()" step="1" class="slider input_range" v-model.number="inputSpeed" :style="{ backgroundSize: speedBackgroundSize }" @change="handleSpeedChange" />
        </div>
      </div>
    </div>

    <div v-if="effectAttach.isBrightness" class="config-container">
      <div class="title">{{ $t('light_brightness') }}</div>
      <div class="config">
        <div class="label_box">
          <span>{{ $t('light_brightness_low') }}</span>
          <span>{{ $t('light_brightness_high') }}</span>
        </div>
        <div class="slider_wrapper">
          <input
            type="range"
            v-if="lightSet.getMinBrightness() > lightSet.getMaxBrightness()"
            :min="lightSet.getMaxBrightness()"
            :max="lightSet.getMinBrightness()"
            step="1"
            class="slider rtl input_range"
            v-model.number="inputBrightness"
            :style="{ backgroundSize: lightBackgroundSize }"
            @change="handleBrightnessChange"
          />

          <input
            type="range"
            v-else
            :min="lightSet.getMinBrightness()"
            :max="lightSet.getMaxBrightness()"
            step="1"
            class="slider input_range"
            v-model.number="inputBrightness"
            :style="{ backgroundSize: lightBackgroundSize }"
            @change="handleBrightnessChange"
          />
        </div>
      </div>
    </div>

    <div v-if="lightSet.effectDirectionList().length > 0" class="color_direction">
      <div class="text">{{ $t('light_direction') }}</div>
      <div class="direction">
        <div v-for="(direction, index) in lightSet.effectDirectionList()" class="direction_item" :class="{ selected: lightSet.directionIsSelected(direction) }" @click="lightSet.changeEffectDirection(direction)">
          <svg viewBox="0 0 24 24" class="direction-svg">
            <path :d="getDirectionPath(direction)" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.light-config-container {
  height: 100%;
  width: 200px; /* 稍微缩小宽度 */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 40px;
  // border-left: 1px solid rgba(0, 0, 0, 0.05);

  /* 窄屏下收缩宽度和内边距，保持并排显示 */
  @media @x-sm {
    width: 180px;
    padding-left: 20px;
  }

  .config-container {
    width: 100%;
    margin-bottom: 24px; /* 缩小间距 */

    .title {
      font-size: 14px;
      font-weight: 700;
      color: var(--color-dark);
      margin-bottom: 16px;
    }

    .config {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;

      .label_box {
        display: flex;
        justify-content: space-between;
        width: 100%;
        color: var(--color-text-hint);
        font-size: 11px;
        font-weight: 500;
      }

      .slider_wrapper {
        position: relative;
        width: 100%;
        height: 16px;
        display: flex;
        align-items: center;
      }

      .slider {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 6px;
        background-color: rgba(0, 0, 0, 0.05);
        border-radius: 3px;
        outline: none;
        background-image: linear-gradient(var(--brand-color-pure), var(--brand-color-pure));
        background-repeat: no-repeat;
        transition: var(--transition-base);

        &.rtl {
          direction: rtl;
        }

        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          background: var(--color-white);
          border: 2px solid var(--color-brand-1);
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          transition: var(--transition-base);

          &:hover {
            transform: scale(1.15);
            box-shadow: 0 4px 12px rgba(242, 110, 62, 0.2);
          }
        }
      }
    }
  }

  .color_direction {
    width: 100%;

    .text {
      font-size: 14px;
      font-weight: 700;
      color: var(--color-dark);
      margin-bottom: 12px;
    }

    .direction {
      display: flex;
      gap: 12px;
      flex-wrap: wrap; /* 允许换行防止溢出裁剪 */
      width: 100%;
      padding: 8px 4px; /* 增加内边距为阴影和位移留出空间 */
      overflow: visible; /* 强制可见，防止被父级容器裁切 */

      .direction_item {
        width: 38px; /* 略微加大尺寸，提升点击体验 */
        height: 38px;
        flex-shrink: 0;
        background: rgba(255, 255, 255, 0.6); /* Glass-like background per instructions 3.3 */
        border-radius: var(--radius-m); /* Unified 12px for interactive controls */
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: var(--transition-base);
        color: var(--color-text-hint);
        line-height: 0;

        .direction-svg {
          display: block;
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          overflow: visible;
          transition: var(--transition-base);
        }

        &:hover {
          transform: translateY(-4px); /* 加大位移反馈 */
          color: var(--brand-color-pure);
          background: var(--color-white);
          box-shadow: var(--action-btn-shadow-hover);
          .direction-svg {
            transform: scale(1.1);
          }
        }

        &.selected {
          background: var(--brand-color);
          color: var(--color-white);
          box-shadow: var(--action-btn-primary-shadow);
          font-weight: 700;
          transform: translateY(-2px); /* 选中态也保持轻微浮起 */
        }
      }
    }
  }
}
</style>
